import { constants } from '@config';
import Store, { actions } from '@store';
import { PageToImageFactory } from 'src/factories/page-to-image';
import { logError } from 'src/utils/logger';

import md5 from '../utils/md5';

function PageThumbnailFactory () {
  this.data = {};
  this.versions = [];
  this.defaultSize = constants.page.thumbnail.allowedSizes.large;

  _.bindAll(this, 'get', 'addThumbnailVersion', 'renderCurrentPage', 'renderPage');
}

PageThumbnailFactory.prototype = _.extend({}, Backbone.Events);

PageThumbnailFactory.prototype.setThumbnail = async function (id, thumbnailUrl) {
  let thumbnail = thumbnailUrl;

  if (thumbnailUrl) {
    try {
      thumbnail = await this.getObjectUrl(thumbnailUrl);
    } catch (error) {
      logError('PageThumbnailFactory')(error);
      thumbnail = await this.renderCurrentPage();
    }

    if (!thumbnail || this.data[id] === thumbnail) return;
  }

  this.data[id] = thumbnail;
  Store.dispatch(actions.page.setPageThumbnail({ id, thumbnail }));
};

PageThumbnailFactory.prototype.getObjectUrl = async function (thumbnailUrl) {
  const protocol = new URL(thumbnailUrl).protocol;
  if (protocol === 'blob:') return thumbnailUrl;

  const response = await fetch(thumbnailUrl);
  if (!response.ok) throw new Error(response.statusText);
  const blob = await response.blob();
  const imageObjectURL = URL.createObjectURL(blob);
  return imageObjectURL;
};

PageThumbnailFactory.prototype.initialize = function () {
  // TODO: Remove unnecessary listeners because of Redux
  if (!this.initializeDeferred) {
    this.initializeDeferred = $.Deferred();

    INKIVE.ProjectFacade.loadPageInfo().then(this.initializeDeferred.resolve);

    INKIVE.ProjectFacade.on('design.added', _.bind(function (data) {
      this.setThumbnail(data.design_id, null);

      if (data.design_id && data.pageSvg) {
        this.renderPage(data.design_id, data.pageSvg);
      } else {
        INKIVE.DesignFacade.once('template.update.designed', this.renderCurrentPage);
        INKIVE.DesignFacade.once('template.update.restricted', this.renderCurrentPage);
        INKIVE.DesignFacade.on('image.update', this.renderCurrentPage);

        _.delay(_.bind(function () {
          INKIVE.DesignFacade.off('image.update', this.renderCurrentPage);
        }, this), 10000);
      }
    }, this));

    INKIVE.ProjectFacade.on('design.copied', _.bind(function (data) {
      this.setThumbnail(data.design_id, this.data[data.based_on]);
    }, this));
  }

  return this.initializeDeferred.promise();
};

PageThumbnailFactory.prototype.get = function (designId) {
  designId = designId || INKIVE.Tool._current_design_id;
  const thumbnail = this.data[designId] || getThumbnailURL.call(this, designId);
  if (this.data[designId] !== thumbnail) this.setThumbnail(designId, thumbnail);
  return thumbnail;
};

PageThumbnailFactory.prototype.notify = function (designId) {
  const design = INKIVE.ProjectFacade.get('design', { design_id: designId });
  if (!design) return;

  this.trigger('update', design);
  this.trigger('update.page.' + design.page_number, design);
  this.trigger('update.design.' + design.design_id, design);
};

PageThumbnailFactory.prototype.renderPage = async function (pageId, pageContent) {
  if (INKIVE.C.IS_MOBILE_APP) return;

  const pageToImageFactory = new PageToImageFactory();
  const thumbnailDataUrl = await pageToImageFactory.getImageDataUrlFromContent(pageContent);
  try {
    const thumbnail = await this.getObjectUrl(thumbnailDataUrl);
    this.setThumbnail(pageId, thumbnail);
    this.storeNewSavedVersion && this.addThumbnailVersion(thumbnail);
    _.defer(_.bind(this.notify, this, pageId));
    return thumbnail;
  } catch (error) {
    logError('renderPage')(error);
  }
};

PageThumbnailFactory.prototype.renderCurrentPage = async function () {
  const pageId = INKIVE.Tool._current_design_id;
  const pageContent = INKIVE.EditorFacade.editor.getSvgString();
  const thumbnail = await this.renderPage(pageId, pageContent);

  return thumbnail;
};

PageThumbnailFactory.prototype.addThumbnailVersion = function (base64ImageData) {
  const newVersion = {
    timeStamp: Date.now(),
    base64: base64ImageData,
    designId: INKIVE.Tool._current_design_id
  };

  this.storeNewSavedVersion = false;
  _.any(this.versions, function (obj) {
    return obj.base64 === base64ImageData;
  }) || this.versions.push(newVersion);
};

PageThumbnailFactory.prototype.checkRenderedImage = function (user) {
  const deferred = $.Deferred();

  const designId = INKIVE.Tool._current_design_id;
  const encodedDesignId = md5(designId);
  const imageSize = '1754';
  const attr = {
    user,
    designId,
    imageUrl: INKIVE.Helpers.getImageUrl(encodedDesignId, imageSize),
    attempts: 0,
    xmlHttp: new XMLHttpRequest(),
    deferred
  };

  checkImgUrl.call(this, attr);
  return deferred.promise();
};

export default new PageThumbnailFactory();

function getThumbnailURL (designOrId) {
  const masterBucket = INKIVE.ProjectFacade.get('master_bucket');
  const design =
    typeof designOrId === 'string'
      ? INKIVE.ProjectFacade.get('design', { design_id: designOrId })
      : designOrId;

  if (masterBucket && design) {
    if (!design.design_id && design.based_on) {
      const originalDesign = INKIVE.ProjectFacade.get('design', { design_id: design.based_on });
      if (originalDesign && originalDesign.design_id) {
        return getThumbnailURL(originalDesign.design_id);
      }
    } else if (design.design_id) {
      const encodedDesign = md5(design.design_id || design.based_on);
      return masterBucket +
        '/' + this.defaultSize + '/' + INKIVE.C.BOOK.id +
        '/' + encodedDesign + '.jpg' +
        '?' + design.last_mod;
    }
  }

  return getTwoCallsThumb(design);
}

function getTwoCallsThumb (design) {
  const now = new Date();

  return INKIVE.C.BASE_URL +
    'design/displaythumbnail/' +
    INKIVE.C.BOOK.id + '/' +
    design.page_number + '/' +
    '110?' + now.getTime();
}

function checkImgUrl (attr) {
  attr.xmlHttp.open('HEAD', attr.imageUrl);
  attr.xmlHttp.onreadystatechange = function () {
    if (attr.xmlHttp.readyState === attr.xmlHttp.DONE ||
      attr.xmlHttp.readyState === attr.xmlHttp.HEADERS_RECEIVED) {
      if (attr.xmlHttp.status === 403) {
        if (attr.attempts === 0) {
          generateImage(attr.designId, attr.user);
        }
        attr.attempts++;

        if (attr.attempts < 5) {
          setTimeout(checkImgUrl.bind(this, attr), 2000);
        } else {
          attr.deferred.resolve(false);
        }
      } else {
        attr.deferred.resolve();
      }
    }
  };
  attr.xmlHttp.send();
}

function generateImage (designId, user) {
  const request = new XMLHttpRequest();
  const authorization = 'Basic ' + btoa(user.credentials.token + ':' + user.credentials.hash);
  const endpoint = INKIVE.C.VIEWER +
    'untidy-api/generate-jpeg';
  const params = 'design_id=' + designId;

  request.open('POST', endpoint);
  request.setRequestHeader('Authorization', authorization);
  request.setRequestHeader('Content-Type', 'application/x-www-form-urlencoded');
  request.send(params);
}
